import { signOut } from 'next-auth/react';
import { toast } from 'sonner';
import { types } from 'util';
import {
  FlashCardDeck,
  FlashCardDeckInput,
  FlashCardDecksResponse,
  FlashCardItem,
  FlashCardItemsResponse,
  FlashCardInput,
  GenerateFlashCardDeckRequest,
  GeneratedFlashCardDeckResponse,
  UpdateFlashCardRequest
} from '@/src/types/flashcard';

interface ApiOptions {
  method?: string;
  body?: any;
  headers?: Record<string, string>;
  isFormData?: boolean;
}

export type FeedbackEntityType = 'QUIZ' | 'FLASHCARD_DECK' | 'COURSE' | 'CARD' | 'QUESTION' | 'LESSON';
export type FeedbackReason = 'INCORRECT' | 'UNCLEAR' | 'DUPLICATE' | 'MEDIA_BROKEN' | 'FORMATTING' | 'OTHER';

export interface UserProfile {
  id: string;
  createdDate: string;
  updatedDate: string;
  firstName: string;
  keycloakId: string;
  email: string;
  groups: string[];
}

export interface Subscription {
  id: string;
  createdDate: string;
  updatedDate: string;
  authorId: string;
  itemSku: string;
  userId: string;
  endDate: string;
  status: string;
}

export interface SubscribableItem {
  name: string;
  parentId: string;
  sku: string;
  price: number;
  duration: number;
  description: string;
}

export interface Plan {
  id: string;
  createdDate: string;
  updatedDate: string;
  authorId: string;
  subscribableItem: SubscribableItem;
  active: boolean;
  name: string;
}

export interface PaymentInitiation {
  qrUrl: string;
  transactionId: string;
}

export interface PaymentStatus {
  id: string;
  createdDate: string;
  updatedDate: string;
  authorId: string;
  status: string;
  amount: number;
  userId: string;
  transactionShortId: string;
  subscribableItems: SubscribableItem[];
  rawMessage: string;
}

export interface ScenarioResponse {
  totalPages: number;
  totalItems: number;
  pageIndex: number;
  pageSize: number;
  data: Array<{
    id: string;
    title: string;
    description: string;
    level: string;
    language: string;
    systemPrompt: string;
  }>;
}

// Store a reference to the navigation function
let navigateFunction: ((path: string) => void) | null = null;
let showUpgradeModal: ((message: string) => void) | null = null;

// Function to set the navigation function from a component
export function setNavigationFunction(navigate: (path: string) => void) {
  navigateFunction = navigate;
}

// Function to set the upgrade modal handler from a component
export function setUpgradeModalHandler(handler: (message: string) => void) {
  showUpgradeModal = handler;
}

class ApiClient {
  private baseUrl: string;
  private accessToken: string | null;

  constructor() {
    this.baseUrl = process.env.NEXT_PUBLIC_API_URL || '';
    this.accessToken = null;
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  async request<T>(
    endpoint: string,
    options: ApiOptions = {}
  ): Promise<T | null> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers: this.getHeaders(options),
        body: options.isFormData
          ? options.body
          : options.body
            ? JSON.stringify(options.body)
            : null
      });

      return this.handleResponse(response) as Promise<T | null>;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async reportFeedback(body: {
    entityType: FeedbackEntityType;
    entityId: string;
    reason: FeedbackReason;
    comment?: string;
  }): Promise<void> {
    await this.request('/learner/feedback', { method: 'POST', body });
  }

  // User Profile API
  async getUserProfile(): Promise<UserProfile | null> {
    return this.request<UserProfile>('/taker/users/me');
  }

  async getUserSubscriptions(): Promise<Subscription[] | null> {
    return this.request<Subscription[]>('/taker/users/subscriptions');
  }

  // Plans and Payment API
  async getPlans(): Promise<Plan[] | null> {
    return this.request<Plan[]>('/public/plans');
  }

  async initiatePayment(planId: string): Promise<PaymentInitiation | null> {
    return this.request<PaymentInitiation>(
      `/taker/payment/initiate/plan/${planId}`,
      {
        method: 'POST'
      }
    );
  }

  async getPaymentStatus(transactionId: string): Promise<PaymentStatus | null> {
    return this.request<PaymentStatus>(
      `/taker/payment/status/${transactionId}`
    );
  }

  // Speech Assessment API
  async submitSpeechAssessment(audioBlob: Blob, script: string) {
    const formData = new FormData();
    formData.append('file', audioBlob);
    formData.append('script', script);

    return this.request('/taker/media/speech-assessment-sync', {
      method: 'POST',
      body: formData,
      isFormData: true
    });
  }

  // IPA API
  async getIpaSymbols() {
    return this.request('/public/ipa/us');
  }

  async getIpaSymbol(id: string) {
    return this.request(`/public/ipa/${id}`);
  }

  // Scenarios API
  async getScenarios(
    page: number = 0,
    size: number = 10
  ): Promise<ScenarioResponse | null> {
    return this.request<ScenarioResponse>(
      `/learner/scenarios?page=${page}&size=${size}`
    );
  }

  // Search scenarios by title and level
  async searchScenarios(
    title?: string,
    level?: string,
    page: number = 0,
    size: number = 10
  ): Promise<ScenarioResponse | null> {
    let url = `/learner/scenarios/search?page=${page}&size=${size}`;

    if (title) {
      url += `&title=${encodeURIComponent(title)}`;
    }

    if (level) {
      url += `&level=${encodeURIComponent(level.toLowerCase())}`;
    }

    return this.request<ScenarioResponse>(url);
  }

  // Start a conversation with a scenario
  async startConversation(
    scenarioId?: string,
    nativeLanguage: string = 'VIETNAMESE',
    targetLanguage: string = 'ENGLISH',
    title?: string,
    description?: string
  ): Promise<ConversationMessage[] | null> {
    return this.request<ConversationMessage[]>(
      '/learner/scenarios/conversations',
      {
        method: 'POST',
        body: {
          nativeLanguage,
          targetLanguage,
          scenarioId: scenarioId,
          title,
          description
        }
      }
    );
  }

  // Get conversation messages
  async getConversationMessages(
    conversationId: string,
    page: number = 0,
    size: number = 10
  ): Promise<ConversationMessagesResponse | null> {
    return this.request<ConversationMessagesResponse>(
      `/learner/scenarios/conversations/messages/by-conversation/${conversationId}?page=${page}&size=${size}&sort=createdDate,desc&sort=id,desc`
    );
  }

  // (other top-level constants can go here)

  // ApiClient class and methods below

  // Get past conversations
  async getPastConversations(
    page: number = 0,
    size: number = 10
  ): Promise<PastConversationsResponse | null> {
    return this.request<PastConversationsResponse>(
      `/learner/scenarios/conversations?page=${page}&size=${size}`
    );
  }

  // Text-to-Speech API
  async getTextToSpeech(
    text: string,
    languageCode: string = 'en-US'
  ): Promise<TtsResponse | null> {
    return this.request<TtsResponse>(
      `/taker/media/tts?language=${languageCode}`,
      {
        method: 'POST',
        body: { value: text }
      }
    );
  }

  // Send a message in a conversation
  async sendMessage(
    conversationId: string,
    message: string
  ): Promise<MessageResponse | null> {
    return this.request<MessageResponse>(
      `/learner/scenarios/conversations/messages/${conversationId}`,
      {
        method: 'POST',
        body: { message }
      }
    );
  }

  // Send a speech message (audio file) in a conversation
  async sendSpeechMessage(
    conversationId: string,
    file: File
  ): Promise<ConversationChatResponseWithUserMessage | null> {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<ConversationChatResponseWithUserMessage>(
      `/learner/scenarios/conversations/messages/speech/${conversationId}`,
      {
        method: 'POST',
        body: formData,
        isFormData: true
      }
    );
  }

  // Get conversation details by ID
  async getConversationDetails(
    conversationId: string
  ): Promise<ConversationDetails | null> {
    return this.request<ConversationDetails>(
      `/learner/scenarios/conversations/${conversationId}`
    );
  }

  // How to say feature
  async howToSay(
    conversationId: string,
    value: string
  ): Promise<HowToSayResponse[] | null> {
    return this.request<HowToSayResponse[]>(
      `/learner/scenarios/conversations/help/how-to-say/${conversationId}`,
      {
        method: 'POST',
        body: { value }
      }
    );
  }

  // Quiz API
  async searchQuizzes(
    tags: string[],
    status: string = 'PUBLISHED',
    page: number = 0,
    size: number = 10
  ): Promise<QuizSearchResponse | null> {
    return this.request<QuizSearchResponse>(
      `/public/quizzes/search?page=${page}&size=${size}`,
      {
        method: 'POST',
        body: { status, tags }
      }
    );
  }

  async getQuizCollectionDetail(
    collectionSlug: string,
    page: number = 0,
    size: number = 10
  ): Promise<QuizCollectionDetail | null> {
    return this.request<QuizCollectionDetail>(
      `/public/quizzes/collections/${collectionSlug}?page=${page}&size=${size}`
    );
  }

  async getQuizDetail(quizId: string): Promise<QuizDetail | null> {
    return this.request<QuizDetail>(`/taker/quizzes/${quizId}/no`);
  }

  async finishQuiz(
    attemptId: string, 
    answers: Record<string, string[]>
  ): Promise<any> {
    return this.request(`/taker/quizzes/finish/${attemptId}`, {
      method: 'POST',
      body: answers
    });
  }

  async getQuizAttemptResults(attemptId: string): Promise<{ quiz: QuizResultsDetail } | null> {
    return this.request<{ quiz: QuizResultsDetail }>(`/taker/quizzes/attempts/${attemptId}/results`);
  }

  // Save quiz progress checkpoint
  async saveQuizProgress(attemptId: string, answers: Record<string, string[]>): Promise<any> {
    return this.request(`/taker/quizzes/attempt-checkpoint/${attemptId}`, {
      method: 'POST',
      body:  answers 
    });
  }

  async getQuizAttempts(
    page: number = 0,
    size: number = 10
  ): Promise<QuizAttemptsResponse | null> {
    return this.request<QuizAttemptsResponse>(
      `/taker/quizzes/list-attempts?page=${page}&size=${size}`
    );
  }

  // Flashcard Deck API
  async getFlashcardDecks(
    page: number = 0,
    size: number = 10,
    source?: string,
    target?: string,
    title?: string,
    itemLevel?: string
  ): Promise<FlashCardDecksResponse | null> {
    let url = `/learner/flashcarddecks?page=${page}&size=${size}`;
    if (source) url += `&source=${source}`;
    if (target) url += `&target=${target}`;
    if (title) url += `&title=${encodeURIComponent(title)}`;
    if (itemLevel) url += `&itemLevel=${itemLevel}`;
    return this.request<FlashCardDecksResponse>(url);
  }

  async createFlashcardDeck(deck: FlashCardDeckInput): Promise<FlashCardDeck | null> {
    return this.request<FlashCardDeck>('/learner/flashcarddecks', {
      method: 'POST',
      body: deck
    });
  }

  async generateFlashcardDeck(
    request: GenerateFlashCardDeckRequest
  ): Promise<GeneratedFlashCardDeckResponse | null> {
    return this.request<GeneratedFlashCardDeckResponse>(
      '/learner/flashcarddecks/generate',
      {
        method: 'POST',
        body: request
      }
    );
  }

  async getFlashcardDeck(id: string): Promise<GeneratedFlashCardDeckResponse | null> {
    return this.request<GeneratedFlashCardDeckResponse>(`/learner/flashcarddecks/${id}`);
  }

  async deleteFlashcardDeck(id: string): Promise<void> {
    await this.request(`/learner/flashcarddecks/${id}`, {
      method: 'DELETE'
    });
  }

  async updateFlashcardDeck(
    id: string,
    data: Partial<FlashCardDeckInput>
  ): Promise<FlashCardDeck | null> {
    return this.request<FlashCardDeck>(`/learner/flashcarddecks/${id}`, {
      method: 'PUT',
      body: data
    });
  }

  async getFlashcardItems(
    deckId: string,
    page: number = 0,
    size: number = 10
  ): Promise<FlashCardItemsResponse | null> {
    return this.request<FlashCardItemsResponse>(
      `/learner/flashcarddecks/${deckId}/items?page=${page}&size=${size}`
    );
  }

  async createFlashcardItems(
    deckId: string,
    cards: FlashCardInput[]
  ): Promise<FlashCardItem[] | null> {
    return this.request<FlashCardItem[]>(`/learner/flashcarddecks/${deckId}/items`, {
      method: 'POST',
      body: { cards }
    });
  }

  async getFlashcardItem(deckId: string, itemId: string): Promise<FlashCardItem | null> {
    return this.request<FlashCardItem>(
      `/learner/flashcarddecks/${deckId}/items/${itemId}`
    );
  }

  async updateFlashcardItem(
    deckId: string,
    itemId: string,
    data: UpdateFlashCardRequest
  ): Promise<FlashCardItem | null> {
    return this.request<FlashCardItem>(
      `/learner/flashcarddecks/${deckId}/items/${itemId}`,
      {
        method: 'PUT',
        body: data
      }
    );
  }

  async deleteFlashcardItem(deckId: string, itemId: string): Promise<void> {
    await this.request(`/learner/flashcarddecks/${deckId}/items/${itemId}`, {
      method: 'DELETE'
    });
  }

  async deleteFlashcardItems(deckId: string, ids: string[]): Promise<void> {
    const queryString = ids.map(id => `ids=${id}`).join('&');
    await this.request(`/learner/flashcarddecks/${deckId}/items?${queryString}`, {
      method: 'DELETE'
    });
  }

  async getSystemFlashcardDecks(
    page: number = 0,
    size: number = 10,
    options?: {
      appId?: string;
      source?: string;
      target?: string;
      title?: string;
    }
  ): Promise<FlashCardDecksResponse | null> {
    let url = `/learner/flashcarddecks/system?page=${page}&size=${size}`;
    if (options?.appId) url += `&appId=${encodeURIComponent(options.appId)}`;
    if (options?.source) url += `&source=${options.source}`;
    if (options?.target) url += `&target=${options.target}`;
    if (options?.title) url += `&title=${encodeURIComponent(options.title)}`;
    return this.request<FlashCardDecksResponse>(url);
  }

  async cloneFlashcardItems(
    targetDeckId: string,
    sourceDeckId: string,
    cardIds: string[]
  ): Promise<void> {
    await this.request(`/learner/flashcarddecks/${targetDeckId}/clone`, {
      method: 'POST',
      body: {
        sourceDeckId,
        cardIds
      }
    });
  }

  async cloneDeck(sourceDeckId: string): Promise<{ deckId: string } | null> {
    return this.request<{ deckId: string }>('/learner/flashcarddecks/clone-deck', {
      method: 'POST',
      body: { sourceDeckId }
    });
  }

  // Courses API
  async listCourses(page: number = 0, size: number = 10): Promise<import('@/src/types/course').PageableResponseMinimalCourse | null> {
    return this.request(`/taker/courses/list?page=${page}&size=${size}`);
  }

  async listMyCourses(page: number = 0, size: number = 10): Promise<import('@/src/types/course').PageableResponseAttemptedCourse | null> {
    return this.request(`/taker/courses/list-mine?page=${page}&size=${size}`);
  }

  async getCourseDetails(courseId: string): Promise<import('@/src/types/course').SimpleCourse | null> {
    return this.request(`/taker/courses/details/${courseId}`);
  }

  async startCourse(courseId: string): Promise<import('@/src/types/course').FullCourseDTO | null> {
    return this.request(`/taker/courses/start/${courseId}`);
  }

  async getCourseLesson(courseId: string, lessonId: string): Promise<import('@/src/types/course').Lesson | null> {
    return this.request(`/taker/courses/${courseId}/lesson/${lessonId}`);
  }

  async markLessonComplete(courseId: string, lessonId: string): Promise<void> {
    await this.request(`/taker/courses/${courseId}/lesson/${lessonId}/complete`, {
      method: 'PUT'
    });
  }

  async submitCourseQuiz(
    courseId: string,
    quizId: string,
    quizAttemptId: string,
    answers: Record<string, string[]>
  ): Promise<any> {
    return this.request(`/taker/courses/${courseId}/quiz/${quizId}/${quizAttemptId}`, {
      method: 'PUT',
      body: answers
    });
  }

  // Starts (or resumes) a course-scoped quiz attempt - needed so the attempt id returned
  // matches what submitCourseQuiz expects (a UserCourseQuizAttempt, not a standalone one).
  async startQuizInCourse(quizId: string, courseId: string): Promise<QuizDetail | null> {
    return this.request<QuizDetail>(`/taker/quizzes/${quizId}/course/${courseId}`, {
      method: 'POST'
    });
  }

  async startQuizInCourseLesson(
    quizId: string,
    courseId: string,
    lessonId: string
  ): Promise<QuizDetail | null> {
    return this.request<QuizDetail>(
      `/taker/quizzes/${quizId}/course/${courseId}/lesson/${lessonId}`,
      { method: 'POST' }
    );
  }

  // Guided Speech API
  async createCustomGuidedSpeechTopic(data: {
    title: string;
    targetLanguage: string;
    nativeLanguage?: string;
    level?: string;
  }): Promise<import('@/src/types/guided-speech').GuidedSpeechTopic | null> {
    return this.request('/learner/guided-speech/topics/custom', {
      method: 'POST',
      body: data
    });
  }

  async getGuidedSpeechTopics(params?: {
    level?: string;
    targetLanguage?: string;
    page?: number;
    size?: number;
  }): Promise<import('@/src/types/guided-speech').PageableResponseGuidedSpeechSessionDTO | null> {
    const q = new URLSearchParams();
    if (params?.level) q.set('level', params.level);
    if (params?.targetLanguage) q.set('targetLanguage', params.targetLanguage);
    q.set('page', String(params?.page ?? 0));
    q.set('size', String(params?.size ?? 20));
    return this.request(`/learner/guided-speech/topics?${q}`);
  }

  async getGuidedSpeechTopic(id: string): Promise<import('@/src/types/guided-speech').GuidedSpeechTopic | null> {
    return this.request(`/learner/guided-speech/topics/${id}`);
  }

  async submitGuidedSpeechRecording(
    topicId: string,
    file: Blob,
    targetLanguage: string
  ): Promise<import('@/src/types/guided-speech').GuidedSpeechSessionDTO | null> {
    const formData = new FormData();
    formData.append('file', file, 'recording.webm');
    return this.request(
      `/learner/guided-speech/topics/${topicId}/submit?targetLanguage=${targetLanguage}`,
      { method: 'POST', body: formData, isFormData: true }
    );
  }

  async getGuidedSpeechTopicSupport(
    id: string,
    targetLanguage: string,
    nativeLanguage: string
  ): Promise<import('@/src/types/guided-speech').TopicSupportResponse | null> {
    return this.request(
      `/learner/guided-speech/topics/${id}/support?targetLanguage=${targetLanguage}&nativeLanguage=${nativeLanguage}`
    );
  }

  async getGuidedSpeechTopicExample(
    id: string,
    targetLanguage: string,
    nativeLanguage: string
  ): Promise<import('@/src/types/guided-speech').Sentence[] | null> {
    return this.request(
      `/learner/guided-speech/topics/${id}/example?targetLanguage=${targetLanguage}&nativeLanguage=${nativeLanguage}`
    );
  }

  async getGuidedSpeechSessions(params?: {
    scenarioId?: string;
    page?: number;
    size?: number;
  }): Promise<import('@/src/types/guided-speech').PageableResponseGuidedSpeechSessionDTO | null> {
    const q = new URLSearchParams();
    if (params?.scenarioId) q.set('scenarioId', params.scenarioId);
    q.set('page', String(params?.page ?? 0));
    q.set('size', String(params?.size ?? 10));
    return this.request(`/learner/guided-speech/sessions?${q}`);
  }

  async getGuidedSpeechSession(id: string): Promise<import('@/src/types/guided-speech').GuidedSpeechSessionDTO | null> {
    return this.request(`/learner/guided-speech/sessions/${id}`);
  }

  // Shadow Scripts API
  async getShadowScriptCategories(): Promise<string[] | null> {
    return this.request('/learner/shadow-scripts/categories');
  }

  async getShadowScripts(params: {
    category: string;
    difficulty: string;
    length?: string;
    language?: string;
    locale?: string;
    page?: number;
    size?: number;
  }): Promise<import('@/src/types/shadow-scripts').PageableResponseShadowScript | null> {
    const q = new URLSearchParams();
    q.set('category', params.category);
    q.set('difficulty', params.difficulty);
    if (params.length) q.set('length', params.length);
    if (params.language) q.set('language', params.language);
    if (params.locale) q.set('locale', params.locale);
    q.set('page', String(params.page ?? 0));
    q.set('size', String(params.size ?? 18));
    return this.request(`/learner/shadow-scripts?${q}`);
  }

  async getShadowScriptDetails(
    scriptId: string
  ): Promise<import('@/src/types/shadow-scripts').ShadowScriptSpeechDetails | null> {
    return this.request(`/learner/shadow-scripts/speech-details/${scriptId}`);
  }

  async explainShadowScript(
    scriptId: string,
    to: string
  ): Promise<import('@/src/types/shadow-scripts').ExplainResponse | null> {
    return this.request(`/learner/shadow-scripts/explain/${scriptId}?to=${to}`);
  }

  async translateShadowScript(
    scriptId: string,
    to: string
  ): Promise<{ value: string } | null> {
    return this.request(`/learner/shadow-scripts/translate/${scriptId}?to=${to}`);
  }

  async getSrsDashboard(
    nativeLanguage: string,
    targetLanguage: string
  ): Promise<import('@/src/types/srs').DashboardResponse | null> {
    return this.request(
      `/learner/srs/dashboard?nativeLanguage=${nativeLanguage}&targetLanguage=${targetLanguage}`
    );
  }

  async getSrsDueCount(
    nativeLanguage: string,
    targetLanguage: string
  ): Promise<import('@/src/types/srs').DueCountResponse | null> {
    return this.request(
      `/learner/srs/review/count?nativeLanguage=${nativeLanguage}&targetLanguage=${targetLanguage}`
    );
  }

  async getSrsReviewQueue(
    nativeLanguage: string,
    targetLanguage: string
  ): Promise<import('@/src/types/srs').ReviewQueueResponse | null> {
    return this.request(
      `/learner/srs/review?nativeLanguage=${nativeLanguage}&targetLanguage=${targetLanguage}`
    );
  }

  async submitSrsReview(
    srsId: string,
    result: import('@/src/types/srs').SrsReviewResult
  ): Promise<import('@/src/types/srs').SubmitReviewResponse | null> {
    return this.request(`/learner/srs/review/${srsId}`, {
      method: 'POST',
      body: { result }
    });
  }

  async getStudySession(
    deckId: string
  ): Promise<import('@/src/types/flashcard-study').StudySessionResponse | null> {
    return this.request(`/learner/flashcard-study/decks/${deckId}/session`);
  }

  async submitCardReview(
    deckId: string,
    cardId: string,
    result: import('@/src/types/flashcard-study').ReviewResult
  ): Promise<import('@/src/types/flashcard-study').ReviewResultResponse | null> {
    return this.request(`/learner/flashcard-study/decks/${deckId}/cards/${cardId}/review`, {
      method: 'POST',
      body: { rating: result }
    });
  }

  async toggleCardStar(
    deckId: string,
    cardId: string
  ): Promise<import('@/src/types/flashcard-study').CardProgressResponse | null> {
    return this.request(`/learner/flashcard-study/decks/${deckId}/cards/${cardId}/star`, {
      method: 'POST'
    });
  }

  async toggleCardSuspend(
    deckId: string,
    cardId: string
  ): Promise<import('@/src/types/flashcard-study').CardProgressResponse | null> {
    return this.request(`/learner/flashcard-study/decks/${deckId}/cards/${cardId}/suspend`, {
      method: 'POST'
    });
  }

  async updateCardFamiliarity(
    deckId: string,
    cardId: string,
    familiarity: import('@/src/types/flashcard-study').UserFamiliarity
  ): Promise<import('@/src/types/flashcard-study').CardProgressResponse | null> {
    return this.request(`/learner/flashcard-study/decks/${deckId}/cards/${cardId}/familiarity`, {
      method: 'PATCH',
      body: { familiarity }
    });
  }

  async getDeckStats(
    deckId: string
  ): Promise<import('@/src/types/flashcard-study').DeckStats | null> {
    return this.request(`/learner/flashcard-study/decks/${deckId}/stats`);
  }

  async getVocabularyJournal(
    statuses: import('@/src/types/vocabulary-journal').NewEntryStatus[],
    page: number,
    size: number
  ): Promise<import('@/src/types/vocabulary-journal').PageableResponseNewEntry | null> {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    statuses.forEach((s) => params.append('statuses', s));
    return this.request(`/learner/new-entries/?${params.toString()}`);
  }

  async createVocabularyEntry(
    request: import('@/src/types/vocabulary-journal').NewEntryRequest
  ): Promise<import('@/src/types/vocabulary-journal').NewEntry | null> {
    return this.request('/learner/new-entries', { method: 'POST', body: request });
  }

  async updateVocabularyEntry(
    entry: import('@/src/types/vocabulary-journal').NewEntry
  ): Promise<import('@/src/types/vocabulary-journal').NewEntry | null> {
    return this.request('/learner/new-entries', { method: 'PUT', body: entry });
  }

  async getVocabularyEntry(
    id: string
  ): Promise<import('@/src/types/vocabulary-journal').NewEntry | null> {
    return this.request(`/learner/new-entries/${id}`);
  }

  async deleteVocabularyEntry(id: string): Promise<void> {
    await this.request(`/learner/new-entries/${id}`, { method: 'DELETE' });
  }

  async dictionaryLookup(
    request: import('@/src/types/dictionary').DictionaryLookupRequest
  ): Promise<import('@/src/types/dictionary').DictionaryItem[] | null> {
    return this.request('/taker/translations/dictionary/lookup', {
      method: 'POST',
      body: request,
    });
  }

  async createTextAnalysis(
    request: import('@/src/types/text-analysis').CreateTextAnalysisRequest
  ): Promise<import('@/src/types/text-analysis').TextAnalysis | null> {
    return this.request('/learner/languages/text-analyses', {
      method: 'POST',
      body: request,
    });
  }

  async listTextAnalyses(
    page = 0,
    size = 10,
    opts: { isSystem?: boolean; tag?: string; targetLanguage?: string; nativeLanguage?: string } = {}
  ): Promise<import('@/src/types/text-analysis').PageableResponseTextAnalysis | null> {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (opts.isSystem !== undefined) params.set('isSystem', String(opts.isSystem));
    if (opts.tag) params.set('tag', opts.tag);
    if (opts.targetLanguage) params.set('targetLanguage', opts.targetLanguage);
    if (opts.nativeLanguage) params.set('nativeLanguage', opts.nativeLanguage);
    return this.request(`/learner/languages/text-analyses?${params.toString()}`);
  }

  async getTextAnalysis(
    id: string
  ): Promise<import('@/src/types/text-analysis').TextAnalysis | null> {
    return this.request(`/learner/languages/text-analyses/${id}`);
  }

  async deleteTextAnalysis(id: string): Promise<void> {
    await this.request(`/learner/languages/text-analyses/${id}`, { method: 'DELETE' });
  }

  async listListeningSets(
    page = 0,
    size = 10
  ): Promise<import('@/src/types/listening-sets').PageableResponseListeningSet | null> {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    return this.request(`/learner/listening-sets?${params.toString()}`);
  }

  async getListeningSet(
    id: string
  ): Promise<import('@/src/types/listening-sets').ListeningSet | null> {
    return this.request(`/learner/listening-sets/${id}`);
  }

  async generateListeningSet(
    params: import('@/src/types/listening-sets').GenerateListeningSetParams
  ): Promise<import('@/src/types/listening-sets').ListeningSet | null> {
    const qs = new URLSearchParams({
      topic: params.topic,
      count: String(params.count),
      targetLanguage: params.targetLanguage,
      nativeLanguage: params.nativeLanguage,
      difficulty: params.difficulty,
    });
    return this.request(`/learner/listening-sets/generate?${qs.toString()}`, { method: 'POST' });
  }

  async deleteListeningSet(id: string): Promise<void> {
    await this.request(`/learner/listening-sets/${id}`, { method: 'DELETE' });
  }

  // Mirror Mode
  async listMirrorSessions(page = 0, size = 12) {
    return this.request<import('../types/mirror').MirrorSessionsResponse>(
      `/learner/llm/mirror/sessions?page=${page}&size=${size}`
    );
  }

  async createMirrorSession(req: import('../types/mirror').CreateMirrorSessionRequest) {
    return this.request<import('../types/mirror').MirrorSession>(
      `/learner/llm/mirror/sessions`,
      { method: 'POST', body: req }
    );
  }

  async getMirrorSession(id: string) {
    return this.request<import('../types/mirror').MirrorSessionDetail>(
      `/learner/llm/mirror/sessions/${id}`
    );
  }

  async deleteMirrorSession(id: string): Promise<void> {
    await this.request(`/learner/llm/mirror/sessions/${id}`, { method: 'DELETE' });
  }

  async sendMirrorMessage(id: string, text: string) {
    return this.request<import('../types/mirror').MirrorSessionMessage>(
      `/learner/llm/mirror/sessions/${id}/messages`,
      { method: 'POST', body: { text } }
    );
  }

  async getNotificationSuggestions(): Promise<NotificationSuggestion[] | null> {
    return this.request<NotificationSuggestion[]>('/learner/notifications/suggestions');
  }

  async dismissNotificationSuggestion(id: string): Promise<void> {
    await this.request(`/learner/notifications/suggestions/${id}/dismiss`, { method: 'PATCH' });
  }

  async searchCategorizedVocabulary(params: CategorizedVocabularySearchParams = {}): Promise<CategorizedVocabulary[] | null> {
    const query = new URLSearchParams();
    if (params.cefrLevel) query.set('cefrLevel', params.cefrLevel);
    if (params.category) query.set('category', params.category);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.size !== undefined) query.set('size', String(params.size));
    const qs = query.toString();
    return this.request<CategorizedVocabulary[]>(`/public/categorized-vocabulary/search${qs ? `?${qs}` : ''}`);
  }

  async listVocabularyCategories(): Promise<string[] | null> {
    return this.request<string[]>('/public/categorized-vocabulary/list-categories');
  }

  private async handleResponse(response: Response) {
    if (response.status === 401) {
      // Clear session and redirect to login
      await signOut({ callbackUrl: '/auth/signin' });
      return null;
    }

    // Parse the response data
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Check for error responses
    if (!response.ok) {
      // Check for subscription required error (code 1010)
      if (data && typeof data === 'object' && data.code === 1010) {
        // Show toast with the error message
        toast.error(data.message);
        // Show upgrade modal if handler is set
        if (showUpgradeModal) {
          showUpgradeModal(data.message);
        }
      }

      const message =
        data && typeof data === 'object' && typeof data.message === 'string'
          ? data.message
          : `HTTP error! status: ${response.status}`;
      throw Object.assign(new Error(message), {
        status: response.status,
        code: data?.code,
        traceId: data?.traceId,
        businessType: data?.type
      });
    }

    return data;
  }

  private getHeaders(options: ApiOptions = {}) {
    const headers: Record<string, string> = {
      ...options.headers
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    if (!options.isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    return headers;
  }
}

// Create a singleton instance
export const apiClient = new ApiClient();

// Add ConversationMessage interface
export interface MessageContent {
  language: string;
  message: string;
}

export interface ConversationMessage {
  id: string;
  createdDate: string;
  updatedDate: string;
  authorId: string;
  conversationId: string;
  role: string;
  message: MessageContent;
  romanization: string;
  translatedMessage: MessageContent;
}

export interface ConversationMessagesResponse {
  totalPages: number;
  totalItems: number;
  pageIndex: number;
  pageSize: number;
  data: ConversationMessage[];
}

export interface PastConversation {
  id: string;
  createdDate: string;
  updatedDate: string;
  authorId: string;
  nativeLanguage: string;
  targetLanguage: string;
  scenarioId: string;
  description: string;
  scenarioSystemDescription: string;
}

export interface PastConversationsResponse {
  totalPages: number;
  totalItems: number;
  pageIndex: number;
  pageSize: number;
  data: PastConversation[];
}

export interface TtsResponse {
  id: string;
  mimeType: string;
  url: string;
}

export interface SuggestedResponse {
  message: string;
  translation: string;
  romanization: string;
}

export interface MessageResponse {
  romanization: string;
  message: string;
  translation: string;
  suggestedResponses: SuggestedResponse[];
}

export interface ConversationChatResponseWithUserMessage {
  userMessage: string;
  chatResponse: MessageResponse;
}

export interface ConversationDetails {
  id: string;
  createdDate: string;
  updatedDate: string;
  authorId: string;
  nativeLanguage: string;
  targetLanguage: string;
  title: string;
  description: string;
  scenarioId: string;
  scenarioSystemDescription: string;
  suggestedResponses: SuggestedResponse[];
}

export interface HowToSayResponse {
  message: string;
  explain: string;
  translation: string;
  romanization?: string;
}

export interface Quiz {
  id: string;
  createdDate: string;
  updatedDate: string;
  authorId: string;
  title: string;
  slug: string;
  subjectId: string;
  tags: string[];
  description: string;
  status: string;
  language: string;
  visibility: string;
  weight: number;
  system: boolean;
  quizIds: string[];
}

export interface QuizSearchResponse {
  totalPages: number;
  totalItems: number;
  pageIndex: number;
  pageSize: number;
  data: Quiz[];
}

export interface IndividualQuiz {
  id: string;
  title: string;
  description?: string;
  durationInSeconds: number;
  language: string;
  level: string;
  averageRating: number;
  totalRatings: number;
  quizAccess: string;
  lastAttemptDate?: string;
  lastScore?: string;
}

export interface QuizCollectionDetail {
  id: string;
  createdDate: string;
  updatedDate: string;
  authorId: string;
  title: string;
  slug: string;
  tags: string[];
  description: string;
  status: string;
  language: string;
  visibility: string;
  weight: number;
  system: boolean;
  quizIds: string[];
  quizzes: {
    totalPages: number;
    totalItems: number;
    pageIndex: number;
    pageSize: number;
    data: IndividualQuiz[];
  };
}

export interface QuizOption {
  uuid: string;
  content: string;
  explanation: string;
  contentType: 'TEXT' | 'AUDIO' | 'VIDEO' | 'IMAGE';
  mediaUrl?: string;
}

export interface QuizQuestion {
  id: string;
  questionGroupId: string;
  content: string;
  type: string;
  point: number;
  createdDate: string;
  options: QuizOption[];
  choiceCount: number;
  correctAnswers?: string[];
}

export interface QuizContentItem {
  id: string;
  mimeType: string;
  content: string;
  mediaUrl?: string;
  type: 'AUDIO' | 'IMAGE' | 'HOSTED_VIDEO' | 'RICH_TEXT';
}

export interface QuizQuestionGroup {
  id: string;
  contentItems: QuizContentItem[];
  questions: QuizQuestion[];
  createdDate: string;
  types: string[];
}

export interface QuizAttempt {
  id: string;
  createdDate: string;
  updatedDate: string;
  takerId: string;
  startTime: string;
  status: string;
  completedTime?: string;
  expectedCompletionTime: string;
  quizId: string;
  quizTitle: string;
  timeLeft: number;
  answersMap?: Record<string, string[]>;
  quizAttemptReport?: QuizAttemptReport;
  inLesson?: boolean;
  lessonId?: string;
  courseId?: string;
  courseAttemptId?: string;
}

export interface QuizDetail {
  id: string;
  title: string;
  description: string;
  tags: string[];
  durationInSeconds: number;
  questionGroups: QuizQuestionGroup[];
  attempt: QuizAttempt;
}

export interface QuizAttemptReport {
  id: string;
  createdDate: string;
  updatedDate: string;
  totalQuestions: number;
  totalCorrectAnswers: number;
  nonAnsweredQuestions: number;
  totalUserInputQuestions: number;
  totalUserInputCorrectAnswers: number;
  totalMultipleChoiceQuestions: number;
  totalMultipleChoiceCorrectAnswers: number;
  userPoints: number;
  totalPoints: number;
  attemptId: string;
  quizId: string;
  takerId: string;
}

export interface QuizAttemptHistory {
  id: string;
  createdDate: string;
  updatedDate: string;
  authorId: string;
  takerId: string;
  startTime: string;
  completedTime?: string;
  status: string;
  expectedCompletionTime: string;
  quizId: string;
  quizTitle: string;
  answersMap?: Record<string, string[]>;
  quizAttemptReport?: QuizAttemptReport;
}

export interface QuizAttemptsResponse {
  totalPages: number;
  totalItems: number;
  pageIndex: number;
  pageSize: number;
  data: QuizAttemptHistory[];
}

export interface QuizResultsAttempt extends QuizAttemptHistory {
  answersMap: Record<string, string[]>;
  quizAttemptReport: QuizAttemptReport;
}

export interface QuizResultsQuestion extends QuizQuestion {
  correctAnswers: string[];
}

export interface QuizResultsQuestionGroup extends Omit<QuizQuestionGroup, 'questions'> {
  questions: QuizResultsQuestion[];
}

export interface QuizResultsDetail {
  id: string;
  title: string;
  description: string;
  tags: string[];
  durationInSeconds: number;
  questionGroups: QuizResultsQuestionGroup[];
  attempt: QuizResultsAttempt;
}

export interface NotificationSuggestion {
  id: string;
  userId: string;
  appId: string;
  suggestionType: 'FLASHCARD_RECAP' | 'DAILY_VOCAB' | 'STREAK_AT_RISK' | 'PRACTICE_REMINDER' | 'INACTIVE_REMINDER';
  targetLanguage: string;
  nativeLanguage: string;
  title: string;
  body: string;
  status: 'ACTIVE' | 'DELIVERED' | 'DISMISSED';
  expiresAt: string;
  createdAt: string;
}

export interface CategorizedVocabulary {
  id: string;
  word: string;
  partOfSpeech: string;
  cefrLevel: string;
  ipa: string;
  definition: string;
  language: string;
  categories: string[];
  examples: string[];
}

export interface CategorizedVocabularySearchParams {
  cefrLevel?: string;
  category?: string;
  page?: number;
  size?: number;
}
